import type { IStore } from "~/state/store";
import type Wasm from "../wasm";
// eslint-disable-next-line no-duplicate-imports
import type { TWasmMapHover } from "../wasm";
import type WasmStoreMapping from "../mapping";
import type CursorManager from "~/util/cursorManager";
import { updateMarker } from "~/state/map-content/state";
import { setHoverSubscription } from "~/state/share-position/state";
import { nearestPointOnLine } from "@turf/nearest-point-on-line";
import { lineString, point } from "@turf/helpers";
import { cleanCoords } from "@turf/clean-coords";
import { length } from "@turf/length";
import { stringToCoords } from "~/util/mirek";
import { standardDistanceFormat } from "~/util/format";
import { createRouteSummaryPopup } from "~/ui/routesummarypopup";

let hoverCursor: object = null;
let summaryGeometryPopup: ReturnType<typeof createRouteSummaryPopup> = null;
let summaryGeometryLine: ReturnType<typeof lineString> = null;
let summaryGeometryTotalLength: number = 0;

function setRouteSummaryPopup(strPoints: string, contactX: number, contactY: number, wasm: Wasm) {
	// pokud jeste neni vytvorena GEOJson geometrie, tak ji vyrobime
	if (!summaryGeometryLine) {
		summaryGeometryLine = cleanCoords(lineString(stringToCoords(strPoints)));
	}

	// pokud jeste neni spocitana delka GEOJson geometrie, tak ji spocitame
	if (!summaryGeometryTotalLength) {
		summaryGeometryTotalLength = length(summaryGeometryLine, { units: "meters" });
	}

	const contactPoint = wasm.coordsFromScreenPosition(contactX, contactY);
	const nearest = nearestPointOnLine(summaryGeometryLine, point([contactPoint.x, contactPoint.y]), { units: "meters" });

	if (!summaryGeometryPopup) {
		summaryGeometryPopup = createRouteSummaryPopup();
		document.body.appendChild(summaryGeometryPopup.container);
	}

	const fromObject = standardDistanceFormat(nearest.properties.location.toString());
	const toObject = standardDistanceFormat(((summaryGeometryTotalLength - nearest.properties.location)).toString());

	summaryGeometryPopup.fromStart.innerHTML = `<strong>${fromObject.distance}</strong>&nbsp;${fromObject.unit}`;
	summaryGeometryPopup.toFinish.innerHTML = `<strong>${toObject.distance}</strong>&nbsp;${toObject.unit}`;
	summaryGeometryPopup.container.style.setProperty("left", `${contactX}px`);
	summaryGeometryPopup.container.style.setProperty("bottom", `${window.innerHeight - contactY}px`);
}

function unsetRouteSummaryPopup() {
	summaryGeometryPopup?.container.remove();
	summaryGeometryPopup = null;
	summaryGeometryLine = null;
	summaryGeometryTotalLength = 0;
}

function useCursor(cursor: string, data: TWasmMapHover, cursorManager: CursorManager) {
	if (data.type === "enter") {
		if (hoverCursor) {
			cursorManager.update(hoverCursor, cursor);
		} else {
			hoverCursor = cursorManager.add(cursor);
		}
	} else if (data.type === "leave") {
		hoverCursor && cursorManager.remove(hoverCursor);
		hoverCursor = null;
	}
}

export function onHover(data: TWasmMapHover, store: IStore, cursorManager: CursorManager, mapping: WasmStoreMapping, wasm: Wasm) {
	//console.log("hover", data.poi ? `${data.poi.source}-${data.poi.id}` : data.wasmId, data.type);
	// vlastni body - hover nad markerem
	if (Mapy.getComponent("userMarks").isActive()) {
		if (data.type !== "move" && data.wasmId) {
			const storeId = mapping.getWasmToStoreId(data.wasmId);
			const { markers } = store.getState().mapContent;
			const userMarksMarker = markers.find(marker => marker.id === storeId);

			if (userMarksMarker?.type === "user-marks") {
				Mapy.getComponent("userMarks").getMain()?.sectionHighlight(data.type === "enter", userMarksMarker.customData?.order - 1);
				useCursor("grab", data, cursorManager);
			}
		}
	} else if (Mapy.getComponent("distanceMeter").isActive() && data.type !== "move" && data.wasmId) {
		const storeId = mapping.getWasmToStoreId(data.wasmId);
		const { markers, geometries } = store.getState().mapContent;
		const distanceMeterMarker = markers.find(marker => marker.id === storeId);
		const distanceMeterGeometry = geometries.find(geometry => geometry.id === storeId);

		if (distanceMeterMarker?.type === "measure-points" || distanceMeterMarker?.type === "measure-start") {
			Mapy.getComponent("distanceMeter").getMain()?.sectionHighlight(data.type === "enter", Math.max(distanceMeterMarker.customData?.index - 1, 0), true);
			useCursor("grab", data, cursorManager);
		}

		if (distanceMeterGeometry?.type === "measure-line") {
			Mapy.getComponent("distanceMeter").getMain()?.sectionHighlight(data.type === "enter", distanceMeterGeometry.customData?.index, true);
			useCursor("url(/img/distancemeter/add.png) 3 3, crosshair", data, cursorManager);
		}
	// nejaky jiny marker nebo geometrie, co jsme si sami dali do mapy
	} else if (data.wasmId) {
		const storeId = mapping.getWasmToStoreId(data.wasmId);
		const { markers, geometries } = store.getState().mapContent;
		const wasmMarker = markers.find(marker => marker.id === storeId);
		const wasmGeometry = geometries.find(geometry => geometry.id === storeId);

		if (wasmMarker && data.type !== "move") {
			// neco, co otevira POI nebo trvale nad mapou a veci z MM nebo aktivita
			if (wasmMarker.customData?.poi
				|| wasmMarker.customData?.dim
				|| wasmMarker.customData?.component === "activities"
				|| wasmMarker.customData?.subscriptionId
			) {
				useCursor("pointer", data, cursorManager);
				// vysledky hledani
				if (wasmMarker.customData.search) {
					Mapy.getComponent("search").hoverItem({
						source: wasmMarker.customData.poi.source,
						id: wasmMarker.customData.poi.id,
						state: data.type,
					});
				// vec z MM, ale ne trvale nad mapou
				} else if (Mapy.getComponent("places").isActive && wasmMarker.customData.dim && wasmMarker.type !== "always-on-map") {
					Mapy.getComponent("places").highlightItem(data.type === "enter" ? wasmMarker.customData.dim : "");
				// aktivita v MM
				} else if (wasmMarker.customData?.component === "activities") {
					Mapy.getComponent("activities").highlight(data.type === "enter" ? wasmMarker.customData.customId : "");
				// sdilena poloha
				} else if (wasmMarker.customData?.subscriptionId) {
					if (data.type === "enter") {
						store.dispatch(setHoverSubscription(wasmMarker.customData.subscriptionId));
					} else {
						store.dispatch(setHoverSubscription(""));
					}
				}
			// neco tahaciho - obvykle nic nedela, jen meni kurzor
			} else if (wasmMarker?.draggable) {
				useCursor("grab", data, cursorManager);
				// routovaci marker
				if (wasmMarker.customData?.route) {
					Mapy.CML.state = {
						route: {
							hoverInd: data.type === "enter" ? wasmMarker.customData.order - 1 : -1,
						},
					};
				}
			}
		}

		// tohle je pojistka pro pripad, kdy dostanu leave na necem, co ale v mape a tedy ani ve store uz neni (stava se pri dnd v route, kdy po presunuti markeru marker z mapy smazeme a preroutujeme, ale mys se nepohne, dokud marker neni z mapy pryc)
		if (data.type === "leave") {
			useCursor("", data, cursorManager);
		}

		if (wasmGeometry) {
			// geometrie routovace
			if (Mapy.getComponent("route").isActive() && wasmGeometry.customData?.route?.routeIndex !== undefined) {
				// aktualni zvolena route reaguje na hover
				if (wasmGeometry.customData?.route && (wasmGeometry.customData.route.routeIndex === Mapy.CML.state.route.index)) {
					// pohyb po geometrii aktualni route
					if (data.type === "move") {
						setRouteSummaryPopup(Mapy.CML.state.route.responseData[Mapy.CML.state.route.index].geometry, data.contactX, data.contactY, wasm);
					// enter/leave na geometrii aktualni route
					} else {
						if (data.type === "enter") {
							setRouteSummaryPopup(Mapy.CML.state.route.responseData[Mapy.CML.state.route.index].geometry, data.contactX, data.contactY, wasm);
							document.body.addEventListener("pointerdown", unsetRouteSummaryPopup);
						} else {
							unsetRouteSummaryPopup();
							document.body.removeEventListener("pointerdown", unsetRouteSummaryPopup);
						}

						markers.forEach(marker => {
							if (marker.customData?.route) {
								store.dispatch(updateMarker({
									id: marker.id,
									data: data.type === "enter"
										? {
											lastChange: marker.lastChange + 1,
											callback: "none",
											visual: "opacity",
											draggable: false,
										}
										: {
											lastChange: marker.lastChange + 1,
											callback: "click-hover",
											visual: "standard",
											draggable: true,
										},
								}));
							}
						});
					}
				// prepinani alternativy v routovaci
				} else {
					useCursor("pointer", data, cursorManager);
				}
			// geometrie neceho z MM
			} else if (data.type !== "move" && Mapy.getComponent("places").isActive && wasmGeometry.customData.dim) {
				Mapy.getComponent("places").highlightItem(data.type === "enter" ? wasmGeometry.customData.dim : "");
				useCursor("pointer", data, cursorManager);
			} else if (wasmGeometry.customData?.component === "activity") {
				if (data.type === "move") {
					setRouteSummaryPopup(Array.isArray(wasmGeometry.strPoints[0]) ? wasmGeometry.strPoints[0][0] : wasmGeometry.strPoints[0], data.contactX, data.contactY, wasm);
				// enter/leave na geometrii detailu aktivity
				} else {
					if (data.type === "enter") {
						setRouteSummaryPopup(Array.isArray(wasmGeometry.strPoints[0]) ? wasmGeometry.strPoints[0][0] : wasmGeometry.strPoints[0], data.contactX, data.contactY, wasm);
						document.body.addEventListener("pointerdown", unsetRouteSummaryPopup);
					} else {
						unsetRouteSummaryPopup();
						document.body.removeEventListener("pointerdown", unsetRouteSummaryPopup);
					}

					markers.forEach(marker => {
						if (marker.customData?.component === "activity") {
							store.dispatch(updateMarker({
								id: marker.id,
								data: data.type === "enter"
									? {
										lastChange: marker.lastChange + 1,
										visual: "opacity",
									}
									: {
										lastChange: marker.lastChange + 1,
										visual: "standard",
									},
							}));
						}
					});
				}
			}
		}
	// hover nad panorama dostupnosti
	} else if (data.rasterLayer === "panoLayer") {
		useCursor("pointer", data, cursorManager);
	// hover nad necim, co otevira POI
	} else if (data.poi && data.type !== "move") {
		useCursor("pointer", data, cursorManager);
	}
}
